import { Drawer } from "antd";
import close from "./../../../../assets/images/close.svg";
import profileIcon from "./../../../../assets/images/profile-dark.svg";
import calendarIcon from "./../../../../assets/images/calender-dark.svg";
import locationIcon from "./../../../../assets/images/location.svg";
import googleLocationIcon from "./../../../../assets/images/google-location.svg";
import cancelIcon from "./../../../../assets/images/cross.svg";
import calenderEdit from "./../../../../assets/images/calendar-edit.svg";
import dayjs from "dayjs";
import { calculateAge, trackEvent } from "../../../../utils/helper";
import CancelConfirmation from "../cancelConfirmation/CancelConfirmation";
import { useState } from "react";
import { AppointmentStatus } from "../homeAppointment/HomeAppointment";
import { getConversation } from "../../service";
import { useNavigate } from "react-router-dom";
import {
  JWT_TOKEN,
  MOBILE_NUMBER,
} from "../../../symptomCollector/utils/constants";
import { getLocalStorage } from "../../../../utils/localStorage";

interface AppointmentDetailsProps {
  show: boolean;
  closePopup: () => void;
  appointmentData: any;
  agentDetails: any;
  avatarImage: any;
}

function parseConversation(rawText: string) {
  if (!rawText) return [];
  const lines = rawText
    .replace(/\\n/g, "\n") // Convert escaped \n to real newlines
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const messages = [];
  let currentType = null;

  for (let line of lines) {
    if (line.startsWith("Agent:")) {
      currentType = "agent";
      const text = line.replace("Agent:", "").trim();
      if (text)
        messages.push({
          type: "agent",
          text,
          timestamp: Date.now(),
        });
    } else if (line.startsWith("User:")) {
      currentType = "user";
      const text = line.replace("User:", "").trim();
      if (text)
        messages.push({
          type: "user",
          text,
          timestamp: Date.now(),
        });
    } else {
      // continuation of previous speaker
      if (currentType) {
        messages.push({
          type: currentType,
          text: line,
          timestamp: Date.now(),
        });
      }
    }
  }

  return messages;
}


const AppointmentDetails = ({
  show,
  closePopup,
  appointmentData,
  agentDetails,
  avatarImage,
}: AppointmentDetailsProps) => {
  const navigate = useNavigate();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const handleCancelAppointment = async () => {
    setShowCancelConfirmation(true);
  };

  const handleEditAppointment = async () => {
    const response = await getConversation({
      pam_id: appointmentData.pam_id,
      um_id: appointmentData.um_id,
      hm_id: appointmentData.hm_id,
      patient_unique_id: appointmentData.patient_unique_id,
    });
    trackEvent("AA_Edit_HS", {
      "Login Mobile No": appointmentData.pm_contact_no,
      "Clinic Id": appointmentData.hm_id,
      "Clinic Name": appointmentData.hospital_details?.[0]?.clinicName,
      "Time Stamp": dayjs().format("YYYY-MM-DD HH:mm:ss"),
      "Owner Doctor Name": appointmentData.um_name,
      "Owner Doctor Specialty": appointmentData.doctor_speciality,
      "Owner Doctor Unique ID": appointmentData.um_id,
      "Patient Name": appointmentData.pm_first_name,
      "Patient Id": appointmentData.pam_pid,
      "Patient Age": calculateAge(appointmentData.pm_dob).years,
      "Patient Gender": appointmentData.pm_gender,
    });
    const jwtToken = getLocalStorage(JWT_TOKEN);
    const phoneNumber = getLocalStorage(MOBILE_NUMBER);
    navigate(`/book-appointment?jwtToken=${jwtToken}`, {
      state: {
        agentDetails: {
          ...agentDetails,
          patient_contact_no: phoneNumber,
        },
        ...appointmentData,
        avatarImage: avatarImage,
        type: appointmentData?.type ?? "chat",
        summary: response?.history?.map((item: any) => item?.summary).join(" "),
        pam_id: appointmentData.pam_id,
        um_id: appointmentData.um_id,
        patient_unique_id: appointmentData.patient_unique_id,
        pm_pid: appointmentData.pm_pid,
        appointment_date: appointmentData.appointment_date,
        appointment_start_time: appointmentData.appointment_start_time,
        appointment_end_time: appointmentData.appointment_end_time,
        appointment_duration: appointmentData.appointment_duration,
        language: response?.language,
        messages: parseConversation(response?.history?.map((item: any) => item?.summary).join(" ")),
      },
    });
  };

  return (
    <Drawer
      placement="bottom"
      onClose={closePopup}
      closeIcon={false}
      open={show}
      height="auto"
      className="rounded-t-2xl overflow-hidden max-w-[450px] mx-auto"
      style={{ margin: "auto", padding: 0 }}
      push={false}
    >
      <div className="flex flex-col h-full p-6 border border-gray-200 rounded-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="font-semibold text-lg text-black">
            Appointment Details
          </div>
          <img
            src={close}
            alt="close"
            onClick={closePopup}
            className="cursor-pointer"
          />
        </div>

        {/* Patient Information */}
        <div className="flex items-start gap-3 mb-4">
          <img src={profileIcon} alt="profile" />
          <span className="text-black">
            {appointmentData.pm_first_name} {appointmentData.pm_last_name || ""}{" "}
            ({appointmentData.pm_gender},{" "}
            {calculateAge(appointmentData.pm_dob).years}yrs)
          </span>
        </div>

        {/* Appointment Time and Doctor */}
        <div className="flex items-start gap-3 mb-4">
          <img src={calendarIcon} alt="calendar" />
          <div>
            <div className="text-black">
              {dayjs(appointmentData.appointment_date).format("DD MMM YY")} |{" "}
              {dayjs(appointmentData.appointment_start_time, "HH:mm:ss").format(
                "hh:mm A"
              )}
            </div>
            <div className="text-gray-600 text-sm">
              With {appointmentData.um_name}
            </div>
          </div>
        </div>

        {/* Clinic Location */}
        <div className="flex items-start gap-3 mb-6">
          <img src={locationIcon} alt="location" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-black">
                {appointmentData.hospital_details?.[0]?.clinicName ||
                  "Clinic Name"}
              </span>
              {appointmentData.hospital_details?.[0]?.googleLocation?.address
                ?.googleMapsLink && (
                <a
                  href={
                    appointmentData.hospital_details?.[0]?.googleLocation
                      ?.address?.googleMapsLink
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 text-purple-500"
                >
                  <img src={googleLocationIcon} alt={"google-location-icon"} />
                </a>
              )}
            </div>
            <div className="text-gray-600 text-sm">
              {appointmentData.hospital_details?.[0]?.googleLocation?.address
                ?.formatted || "Address not available"}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {appointmentData?.pam_status !== AppointmentStatus.FINISHED &&
          appointmentData?.pam_status !== AppointmentStatus.CANCEL && (
            <div className="flex gap-3 mt-2">
              <button
                className="flex-1 flex items-center justify-center gap-2 text-red-500 text-sm font-medium"
                onClick={handleCancelAppointment}
              >
                <img src={cancelIcon} alt="cancel" />
                Cancel
              </button>
              <div className="w-[1px] bg-gray-300"></div>
              <button
                className="flex-1 flex items-center justify-center gap-2 text-[#4B4AD5] text-sm font-medium"
                onClick={handleEditAppointment}
              >
                <img src={calenderEdit} alt="calender-edit" />
                Edit Appointment
              </button>
            </div>
          )}
      </div>
      {showCancelConfirmation && (
        <CancelConfirmation
          show={showCancelConfirmation}
          closePopup={() => setShowCancelConfirmation(false)}
          appointmentData={appointmentData}
        />
      )}
    </Drawer>
  );
};

export default AppointmentDetails;
